const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const uniqueIds = (values = []) => Array.from(
  new Set(values.map((value) => Number(value)).filter((value) => value > 0))
);

const normalizeWarehouseRow = (row = {}) => {
  const warehouseId = Number(row.warehouseId ?? row.warehouse_id ?? row.id ?? 0);
  const target = toNumber(row.target ?? row.low_stock_threshold);
  const current = toNumber(row.current ?? row.piece);
  const ordered = toNumber(row.ordered ?? row.ordered_quantity);
  const grossNeed = toNumber(
    row.grossNeed ?? row.gross_need ?? Math.max(target - current, 0)
  );
  const netNeed = toNumber(
    row.netNeed ?? row.net_need ?? Math.max(grossNeed - ordered, 0)
  );

  return {
    warehouseId,
    name: row.name || null,
    kind: row.kind || null,
    departmentId: row.departmentId ?? row.department_id ?? null,
    consumptionPriority: row.consumptionPriority ?? row.consumption_priority ?? null,
    target,
    current,
    ordered,
    grossNeed,
    allocatedOrdered: toNumber(row.allocatedOrdered ?? row.allocated_ordered),
    netNeed,
    allocatedQuantity: toNumber(row.allocatedQuantity ?? row.allocated_quantity),
  };
};

const calculateTotals = (warehouses, scopeWarehouseIds, fallbackTotals = {}) => {
  const scope = new Set(scopeWarehouseIds);
  const scopedRows = warehouses.filter((row) => scope.has(row.warehouseId));

  return {
    target: scopedRows.reduce((sum, row) => sum + row.target, 0),
    current: scopedRows.reduce((sum, row) => sum + row.current, 0),
    ordered: toNumber(fallbackTotals.ordered),
    grossNeed: scopedRows.reduce((sum, row) => sum + row.grossNeed, 0),
    netNeed: scopedRows.reduce((sum, row) => sum + row.netNeed, 0),
  };
};

export const normalizeCartStockSnapshot = (source = {}) => {
  const raw = source?.need_context || source?.needContext || source || {};
  let warehouses = Array.isArray(raw.warehouses)
    ? raw.warehouses.map(normalizeWarehouseRow).filter((row) => row.warehouseId)
    : [];

  // Legacy single-warehouse snapshots are safe to upgrade. A legacy aggregate
  // scope without a concrete warehouse is intentionally not guessed.
  if (!warehouses.length && raw.warehouseId) {
    warehouses = [normalizeWarehouseRow({
      warehouseId: raw.warehouseId,
      target: raw.target,
      current: raw.current,
      ordered: raw.ordered,
      netNeed: raw.netNeed,
    })];
  }

  const backendTotals = raw.totals || {};
  const requestedScopeIds = uniqueIds(raw.scopeWarehouseIds || raw.scope_warehouse_ids || []);
  const actualIds = warehouses.map((row) => row.warehouseId);
  // Missing stock rows must not erase an explicit scope. Otherwise the next
  // reconciliation treats an empty scope as all warehouses and redirects it.
  const scopeWarehouseIds = requestedScopeIds.length
    ? requestedScopeIds
    : actualIds;
  const totals = calculateTotals(warehouses, scopeWarehouseIds, {
    ordered: backendTotals.ordered ?? raw.ordered,
  });

  if (backendTotals.target !== undefined) totals.target = toNumber(backendTotals.target);
  if (backendTotals.current !== undefined) totals.current = toNumber(backendTotals.current);
  if (backendTotals.ordered !== undefined) totals.ordered = toNumber(backendTotals.ordered);
  if (backendTotals.gross_need !== undefined || backendTotals.grossNeed !== undefined) {
    totals.grossNeed = toNumber(backendTotals.gross_need ?? backendTotals.grossNeed);
  }
  if (backendTotals.net_need !== undefined || backendTotals.netNeed !== undefined) {
    totals.netNeed = toNumber(backendTotals.net_need ?? backendTotals.netNeed);
  } else if (raw.netNeed !== undefined) {
    totals.netNeed = toNumber(raw.netNeed);
  }

  const defaultStockWarehouseId = Number(
    raw.defaultStockWarehouseId ?? raw.default_stock_warehouse_id ?? 0
  ) || null;

  return {
    version: 2,
    mode: scopeWarehouseIds.length === 1 ? "single" : "multi",
    defaultStockWarehouseId,
    warehouseId: scopeWarehouseIds.length === 1 ? scopeWarehouseIds[0] : null,
    scopeWarehouseIds,
    target: totals.target,
    current: totals.current,
    ordered: totals.ordered,
    netNeed: totals.netNeed,
    totals,
    warehouses,
    overflowQuantity: toNumber(raw.overflowQuantity),
  };
};

export const buildSingleWarehouseSnapshot = ({
  warehouse,
  defaultStockWarehouseId,
  quantity = 0,
}) => {
  const row = normalizeWarehouseRow(warehouse || {});
  if (!row.warehouseId) {
    return normalizeCartStockSnapshot({});
  }

  return allocateSnapshotQuantity(
    normalizeCartStockSnapshot({
      version: 2,
      defaultStockWarehouseId,
      scopeWarehouseIds: [row.warehouseId],
      warehouses: [row],
      totals: {
        target: row.target,
        current: row.current,
        ordered: row.ordered,
        grossNeed: row.grossNeed,
        netNeed: row.netNeed,
      },
    }),
    quantity
  );
};

export const mergeCartStockSnapshots = (existing, incoming) => {
  const left = normalizeCartStockSnapshot(existing);
  const right = normalizeCartStockSnapshot(incoming);
  const rows = new Map();

  left.warehouses.forEach((row) => rows.set(row.warehouseId, { ...row }));
  right.warehouses.forEach((row) => {
    const previous = rows.get(row.warehouseId);
    rows.set(row.warehouseId, {
      ...(previous || {}),
      ...row,
      allocatedQuantity: toNumber(previous?.allocatedQuantity) + toNumber(row.allocatedQuantity),
    });
  });

  const scopeWarehouseIds = uniqueIds([
    ...left.scopeWarehouseIds,
    ...right.scopeWarehouseIds,
  ]);
  const preferred = right.warehouses.length >= left.warehouses.length ? right : left;

  return normalizeCartStockSnapshot({
    version: 2,
    defaultStockWarehouseId:
      right.defaultStockWarehouseId || left.defaultStockWarehouseId,
    scopeWarehouseIds,
    totals: preferred.totals,
    warehouses: Array.from(rows.values()),
    overflowQuantity:
      toNumber(left.overflowQuantity) + toNumber(right.overflowQuantity),
  });
};

export const allocateSnapshotQuantity = (snapshot, quantity) => {
  const normalized = normalizeCartStockSnapshot(snapshot);
  const scope = new Set(normalized.scopeWarehouseIds);
  let remaining = Math.max(toNumber(quantity), 0);

  const warehouses = normalized.warehouses.map((row) => {
    const allowed = scope.has(row.warehouseId);
    const allocatedQuantity = allowed ? Math.min(row.netNeed, remaining) : 0;
    remaining -= allocatedQuantity;
    return { ...row, allocatedQuantity };
  });

  return {
    ...normalized,
    warehouses,
    overflowQuantity: Math.max(remaining, 0),
  };
};

const broadestContext = (items) => items
  .map((item) => normalizeCartStockSnapshot(item.stockSnapshot))
  .sort((left, right) => right.warehouses.length - left.warehouses.length)[0]
  || normalizeCartStockSnapshot({});

export const reconcileProductCartItems = (items, liveContext = null) => {
  if (!liveContext && !items.some((item) => item.stockSnapshot)) {
    return items;
  }

  const context = liveContext
    ? normalizeCartStockSnapshot(liveContext)
    : broadestContext(items);
  if (!context.warehouses.length) {
    return items;
  }
  const remainingByWarehouse = new Map(
    context.warehouses.map((row) => [row.warehouseId, row.netNeed])
  );

  return items.map((item) => {
    const itemSnapshot = normalizeCartStockSnapshot(item.stockSnapshot);
    const scopeIds = itemSnapshot.scopeWarehouseIds.length
      ? itemSnapshot.scopeWarehouseIds
      : context.scopeWarehouseIds;
    const scope = new Set(scopeIds);
    let remainingQuantity = Math.max(toNumber(item.quantity), 0);

    const warehouses = context.warehouses.map((row) => {
      const available = Math.max(toNumber(remainingByWarehouse.get(row.warehouseId)), 0);
      const allocatedQuantity = scope.has(row.warehouseId)
        ? Math.min(available, remainingQuantity)
        : 0;
      remainingQuantity -= allocatedQuantity;
      remainingByWarehouse.set(row.warehouseId, available - allocatedQuantity);
      return { ...row, allocatedQuantity };
    });

    const snapshot = normalizeCartStockSnapshot({
      version: 2,
      defaultStockWarehouseId: context.defaultStockWarehouseId,
      scopeWarehouseIds: scopeIds,
      warehouses,
      totals: {
        ordered: context.totals.ordered,
      },
      overflowQuantity: remainingQuantity,
    });
    snapshot.warehouses = warehouses;
    snapshot.overflowQuantity = Math.max(remainingQuantity, 0);

    return { ...item, stockSnapshot: snapshot };
  });
};

export const reconcileCartNeedAllocations = (cart, contextsByProductId = {}) => {
  const productIds = uniqueIds(cart.map((item) => item.product?.id));
  const reconciledById = new Map();

  productIds.forEach((productId) => {
    const productItems = cart.filter(
      (item) => Number(item.product?.id) === productId
    );
    const liveContext = contextsByProductId[productId] || contextsByProductId[String(productId)];
    reconcileProductCartItems(productItems, liveContext).forEach((item) => {
      reconciledById.set(item.id, item);
    });
  });

  return cart.map((item) => reconciledById.get(item.id) || item);
};

export const getMissingQuantityForContext = (context, cartItems) => {
  const normalized = normalizeCartStockSnapshot(context);
  const reconciled = reconcileProductCartItems(cartItems, normalized);
  const scope = new Set(normalized.scopeWarehouseIds);
  const allocated = reconciled.reduce(
    (sum, item) => sum + item.stockSnapshot.warehouses.reduce(
      (rowSum, row) => rowSum + (
        scope.has(row.warehouseId) ? toNumber(row.allocatedQuantity) : 0
      ),
      0
    ),
    0
  );

  return Math.max(normalized.totals.netNeed - allocated, 0);
};

export const getProductOverflowQuantity = (items) => reconcileProductCartItems(items)
  .reduce((sum, item) => sum + toNumber(item.stockSnapshot?.overflowQuantity), 0);

export const getAllocatedWarehouseNeeds = (snapshot) => normalizeCartStockSnapshot(snapshot)
  .warehouses
  .filter((warehouse) => warehouse.allocatedQuantity > 0)
  .map((warehouse) => ({
    warehouseId: warehouse.warehouseId,
    name: warehouse.name,
    quantity: warehouse.allocatedQuantity,
  }));

export const getOrderWarehouseAllocations = (snapshot, quantity) => {
  const normalized = normalizeCartStockSnapshot(snapshot);
  const defaultWarehouseId = normalized.defaultStockWarehouseId;
  let remaining = Math.max(toNumber(quantity), 0);

  if (!remaining) return [];

  const allocations = new Map();
  const orderedWarehouses = [...normalized.warehouses].sort((left, right) => {
    const leftIsDefault = left.warehouseId === defaultWarehouseId ? 1 : 0;
    const rightIsDefault = right.warehouseId === defaultWarehouseId ? 1 : 0;
    return rightIsDefault - leftIsDefault;
  });

  orderedWarehouses.forEach((warehouse) => {
    if (remaining <= 0 || warehouse.allocatedQuantity <= 0) return;
    const allocated = Math.min(warehouse.allocatedQuantity, remaining);
    allocations.set(
      warehouse.warehouseId,
      toNumber(allocations.get(warehouse.warehouseId)) + allocated
    );
    remaining -= allocated;
  });

  // A newly selected default may not have a stock assignment yet. Live need
  // contexts only contain assigned warehouses; that must not redirect excess.
  const fallbackWarehouseId = defaultWarehouseId || orderedWarehouses[0]?.warehouseId;
  if (remaining > 0 && fallbackWarehouseId) {
    allocations.set(
      fallbackWarehouseId,
      toNumber(allocations.get(fallbackWarehouseId)) + remaining
    );
  }

  return Array.from(allocations.entries()).map(([warehouseId, allocatedQuantity]) => ({
    warehouse_id: warehouseId,
    quantity: allocatedQuantity,
  }));
};

export const getDefaultWarehouseTarget = (snapshot) => {
  const normalized = normalizeCartStockSnapshot(snapshot);
  const row = normalized.warehouses.find(
    (warehouse) => warehouse.warehouseId === normalized.defaultStockWarehouseId
  );

  return {
    warehouseId: normalized.defaultStockWarehouseId,
    name: row?.name || null,
    target: toNumber(row?.target),
  };
};

